import { useEffect, useRef, useState } from 'react';
import ShelfQrScanner from './ShelfQrScanner';
import { vintedIntake, vintedFoto, vintedLookup, notiz, istPaketCode, vintedCode, autoFach, vorladenStart, vorladenStand, vt } from '../services/vinted';
import { platzhalterName, formatCode, normCode, isVintedSortOn, vintedFach } from '../services/vintedNames';
import { isAutoRegalLetter } from '../services/printerService';
import { autoRegalFach } from '../services/regalUnterfach';
import { uploadFoto } from '../services/mlService';
import { isTurboOn } from '../services/turbo';
import TurboAnzeige from './TurboAnzeige';
import FotoStand, { useFotoZaehler, fotosOffen } from './FotoStand';
import { mitWiederholung } from '../services/serverWarte';

// Foto (Daten-URL vom Scanner) in der Bild-Ablage sichern, Ergebnis ist die Bild-ID fuers Paket; klappt es nicht, bleibt das Paket ohne Foto
async function fotoSichern(dataUrl) {
  try { return await uploadFoto(await (await fetch(dataUrl)).blob()); } catch { return ''; }
}

// Tonsignale (Wunsch von Folkert, 05.10.2026): kurzer hoher Ton bei Erfolg, tiefer Brummton bei Problemen
let audioCtx = null;
function ton(freq, ms, type = 'sine') {
  try {
    audioCtx = audioCtx || new (window.AudioContext || window.webkitAudioContext)();
    const osc = audioCtx.createOscillator();
    const gain = audioCtx.createGain();
    osc.type = type; osc.frequency.value = freq; gain.gain.value = 0.2;
    osc.connect(gain); gain.connect(audioCtx.destination);
    osc.start(); osc.stop(audioCtx.currentTime + ms / 1000);
  } catch { /* kein Ton möglich */ }
}
// GLS-Pakete innerhalb von Vinted (Partner-Etikett „GLS Parcels to People", Strichcode mit 11–12 Ziffern, Tim 09.10.2026):
// im Annahme-Dialog zählen sie als Paket; der Kurzcode fürs Etikett sind wie bei Vinted die letzten 6 Ziffern.
const istGlsCode = (c) => /^\d{11,12}$/.test(String(c || ''));
const tonOk = () => ton(880, 120);
const tonFehler = () => ton(220, 450, 'square');
// Angenommen, aber ohne Fach (kein Name → kein automatisches Fach): doppelter Hinweiston, damit der
// Mitarbeiter es auch ohne Hinschauen merkt und das Paket nicht ohne Vak einlagert (Folkert 13.10.2026).
const tonWarn = () => { ton(660, 130); setTimeout(() => ton(660, 130), 180); };

/**
 * 🧪 „Vinted erfassen" (Experiment): Vinted-Etiketten nacheinander scannen. Jeder Code geht im Hintergrund
 * an die Vinted-App (Annahme „Driver delivery"); kommt der Name zurück, wird das Paket mit Namen angelegt.
 * Rückfall: antwortet Vinted nicht oder steht das Paket danach nicht im Vinted-Bestand, wird es trotzdem
 * angelegt (Platzhalter „Vinted 123-456") und als „bitte in der Vinted-App scannen" markiert.
 * Codes, die kein Paket sind (z. B. das Sack-Etikett), werden nur an Vinted weitergegeben.
 * Nach erfolgreicher Annahme geht das volle Paketfoto (bis 1280 px) im Hintergrund an Vinted Go, damit sowohl in
 * PaketPilot als auch in der Vinted-Go-App das ganze Paket zum Wiederfinden im Regal gespeichert ist.
 */
export default function VintedErfassen({ pakete, colors, language, onAnlegen, onNamen, onClose }) {
  const T = vt(language);
  const dark = colors?.darkMode;
  const f = { text: dark ? '#fff' : '#23272f', sub: dark ? '#aaa' : '#666', rand: dark ? '#444' : '#ddd', feld: dark ? '#1b1f27' : '#fff', zeile: dark ? '#2d333b' : '#f5f6f8' };

  const [fach, setFach] = useState('');
  const [hand, setHand] = useState('');
  const [eintraege, setEintraege] = useState([]); // {code, stand, name, grund}
  // Die Verarbeitung läuft nacheinander und über mehrere Renders → immer die aktuellen Werte nehmen
  const fachRef = useRef(''); fachRef.current = fach;
  const paketeRef = useRef(pakete); paketeRef.current = pakete;
  const anlegenRef = useRef(onAnlegen); anlegenRef.current = onAnlegen;
  const namenRef = useRef(onNamen); namenRef.current = onNamen;
  const gesehen = useRef(new Set());
  const fotos = useRef(new Map()); // Code → Foto vom Erkennen (geht mit an Vinted, wird danach verworfen)
  const kette = useRef(Promise.resolve());
  const fotoKette = useRef(Promise.resolve()); // 📷 Foto-Uploads nacheinander im Hintergrund, damit sie nie vintedIntake blockieren
  const aktiveScans = useRef(0);
  const letzteScanAktivitaet = useRef(0); // ⏱️ 4s Ruhe nach jedem Scan abwarten, bevor Fotos die Leitung belegen (Folkert Handy B, 27.10.2026)
  const [fotoStand, verfolgeFoto] = useFotoZaehler(); // 📷 Uploads im Hintergrund (Vinted-Foto + PaketPilot-Foto)
  const lebt = useRef(true);
  useEffect(() => () => { lebt.current = false; }, []);
  // ⚡ Turbo (Experiment): Foto im Hintergrund, großes Fach, Zähler, Zeitmessung je Paket
  const [turbo, setTurboState] = useState(() => isTurboOn());
  useEffect(() => {
    const sync = () => setTurboState(isTurboOn());
    window.addEventListener('kiosk:profile-applied', sync);
    return () => window.removeEventListener('kiosk:profile-applied', sync);
  }, []);
  const reiheFotoEin = (fn) => {
    const p = fotoKette.current.then(async () => {
      while (aktiveScans.current > 0 || (Date.now() - letzteScanAktivitaet.current < 4000)) await new Promise((res) => setTimeout(res, 120));
      return await fn();
    }).catch(() => false);
    fotoKette.current = p.then(() => {}, () => {});
    verfolgeFoto(p);
  };
  const [turboStand, setTurboStand] = useState({ letzter: null, gebucht: 0, ausnahmen: 0, summeMs: 0, n: 0 });
  const scanZeit = useRef(new Map()); // Code → Zeitpunkt des Scans
  const turboMelden = (letzter, dauerMs) => {
    if (!turbo || !lebt.current) return;
    setTurboStand((s) => ({
      letzter,
      gebucht: s.gebucht + (letzter.ausnahme === 'fremd' || letzter.ausnahme === 'doppelt' ? 0 : 1),
      ausnahmen: s.ausnahmen + (letzter.ausnahme ? 1 : 0),
      summeMs: s.summeMs + (dauerMs || 0), n: s.n + (dauerMs ? 1 : 0),
    }));
  };

  // Beim Öffnen die Namen der Pakete laden lassen, die bei Vinted auf den Scan warten. Mit den Namen im Speicher
  // dauert ein Scan Sekunden statt einer Viertelminute (ohne müsste nach jedem Paket im Bestand nachgeschlagen werden).
  const [vor, setVor] = useState(null); // null | { laeuft, s } | { done, ok, total, read, partial }
  useEffect(() => {
    let aktiv = true;
    (async () => {
      const start = await vorladenStart();
      if (!aktiv) return;
      if (!start.ok) { setVor({ done: true, ok: false }); return; }
      const t0 = Date.now();
      setVor({ laeuft: true, s: 0 });
      while (aktiv) {
        await new Promise((r) => setTimeout(r, 2500));
        const stand = await vorladenStand();
        if (!aktiv) return;
        if (stand.done) { setVor(stand); return; }
        if (stand.ok === false && stand.reason !== 'offline') { setVor({ done: true, ok: false }); return; }
        setVor({ laeuft: true, s: Math.round((Date.now() - t0) / 1000) });
      }
    })();
    return () => { aktiv = false; };
  }, []);
  // Brach das Vorladen ab, weil sofort gescannt wurde (die Annahme hat Vorrang), nach einer echten Scan-Pause von
  // 25 s von selbst noch einmal anstoßen — so sind die Namen für den nächsten Sack da, ohne den Dialog neu zu öffnen.
  // Gebremst (09.10.2026): mit 8 s Pause und ohne Grenze lief es bei Folkert 49-mal an und wurde jedes Mal vom nächsten
  // Scan abgebrochen — jeder Abbruch kostete den Emulator ~15 s und machte die Scans langsamer (12 s statt 7 s).
  // Darum: höchstens 3 Versuche je Dialog, mindestens 3 Minuten Abstand.
  const vorRef = useRef(vor); vorRef.current = vor;
  const nachlader = useRef(null);
  const nachladeStand = useRef({ versuche: 0, zuletzt: 0 });
  const vorladenNachPause = () => {
    clearTimeout(nachlader.current);
    nachlader.current = setTimeout(async () => {
      const v = vorRef.current;
      if (!lebt.current || !v || !v.done || !v.ok || !v.partial) return;
      const st = nachladeStand.current;
      if (st.versuche >= 3 || Date.now() - st.zuletzt < 3 * 60 * 1000) return;
      st.versuche++; st.zuletzt = Date.now();
      const start = await vorladenStart();
      if (!lebt.current || !start.ok) return;
      const t0 = Date.now();
      setVor({ laeuft: true, s: 0 });
      while (lebt.current) {
        await new Promise((r) => setTimeout(r, 2500));
        const stand = await vorladenStand();
        if (!lebt.current) return;
        if (stand.done) { setVor(stand); return; }
        if (stand.ok === false && stand.reason !== 'offline') { setVor({ done: true, ok: false }); return; }
        setVor({ laeuft: true, s: Math.round((Date.now() - t0) / 1000) });
      }
    }, 25000);
  };
  useEffect(() => () => clearTimeout(nachlader.current), []);
  const vorText = !vor ? '' : vor.laeuft ? `⏳ ${T('vorLaeuft', { s: vor.s })}` : !vor.ok ? `⚠️ ${T('vorFehler')}`
    : !vor.total ? `ℹ️ ${T('vorLeer')}` : vor.partial ? `⚠️ ${T('vorTeil', { n: vor.read, m: vor.total })}` : `✅ ${T('vorOk', { n: vor.read })}`;

  const setze = (code, patch) => { if (lebt.current) setEintraege((alt) => alt.map((e) => (e.code === code ? { ...e, ...patch } : e))); };

  const verarbeite = async (code) => {
    const tStart = Date.now(); // ⏱️ Messung 22.10.2026: wo hängen die ~3 s zwischen Scan und Buchung (Server nur 0,8 s)?
    const paket = istPaketCode(code) || istGlsCode(code);
    const gespeichert = fotos.current.get(code) || {};
    const foto = typeof gespeichert === 'string' ? gespeichert : (gespeichert.foto || '');
    const vollFoto = typeof gespeichert === 'string' ? gespeichert : (gespeichert.vollFoto || foto);
    fotos.current.delete(code);
    const t0 = scanZeit.current.get(code) || Date.now();
    scanZeit.current.delete(code);
    // Zuerst NUR den Code buchen (winzig → Name kommt sofort). Die 1280px-Fotos (Vinted + PaketPilot) werden erst NACH
    // der Buchung und dem Anlegen nacheinander im Hintergrund hochgeladen (27.10.2026: sonst verstopfen zwei parallele
    // 1280px-Uploads die Mobilfunk-/WLAN-Leitung für 5–11 s und bremsen sowohl dieses als auch das nächste Paket aus).
    const tAnfrage = Date.now();
    // ⏳ Server kurz weg (Deploy/WLAN)? Scan bleibt in der Schlange und wird nachgeholt statt mit Platzhalter angelegt
    const r = await mitWiederholung(() => vintedIntake(code, undefined, undefined), { onWarte: ({ versuch }) => setze(code, { stand: 'wartet', grund: T('serverWarte', { n: versuch }) }) });
    setze(code, { grund: '' });
    const tGebucht = Date.now();
    letzteScanAktivitaet.current = tGebucht;
    const kurzAnzeige = formatCode(code);
    if (!paket) {
      if (vollFoto && r && r.ok) reiheFotoEin(() => mitWiederholung(() => vintedFoto(code, r.id, vollFoto)).then((x) => !!(x && x.ok), () => false));
      (r.reacted ? tonOk : tonFehler)();
      setze(code, { stand: r.reacted ? 'sack' : 'sackFehler', grund: r.reason || '' });
      return;
    }
    // Laut Vinted nicht für diesen Shop → nicht anlegen, es kommt in den Sack für den Fahrer
    if (r.reason === 'fremd') { tonFehler(); setze(code, { stand: 'fremd', grund: '' }); turboMelden({ fach: '🚚', name: '', kurz: kurzAnzeige, ausnahme: 'fremd' }); return; }
    const kurz = normCode(code);
    const name = r.ok && r.name ? r.name : platzhalterName(kurz);
    // Fach: was oben eingetragen ist; sonst automatisch wie beim normalen Erfassen (Nachname bzw. Vinted-Nummer)
    const fach = fachRef.current.trim() || autoFach(kurz, name, { vintedSort: isVintedSortOn(), autoLetter: isAutoRegalLetter(), fachAusName: autoRegalFach, fachAusNummer: vintedFach });
    try {
      // Immer sofort anlegen (und drucken + Ton), ohne auf den 1280px-Foto-Upload zu warten — auch ohne Turbo-Schalter!
      const neu = await anlegenRef.current({ tracking: code, name, regalplatz: fach, imageUrl: '' });
      const tAngelegt = Date.now();
      letzteScanAktivitaet.current = tAngelegt;
      if (vollFoto && r && r.ok) {
        reiheFotoEin(() => mitWiederholung(() => vintedFoto(code, r.id, vollFoto)).then((x) => !!(x && x.ok), () => false));
      }
      if (paket && vollFoto && neu && neu.id) {
        reiheFotoEin(async () => {
          const id = await fotoSichern(vollFoto);
          if (!id || !namenRef.current) return !!id;
          const p = (paketeRef.current || []).find((x) => x && x.id === neu.id) || neu;
          await namenRef.current([{ paket: p, code: kurz, name: p.name || name, imageUrl: id, nurFoto: true }]);
          return true;
        });
      }
      const zeiten = turbo ? ` · Turbo: gebucht ${tGebucht - t0} ms, angelegt ${tAngelegt - t0} ms · warte ${tStart - t0}, vorbereiten ${tAnfrage - tStart}, anfrage ${tGebucht - tAnfrage}, server ${r && r.ms != null ? r.ms : '?'}, anlegen ${tAngelegt - tGebucht}` : '';
      notiz('annahme_paketpilot', code, { ok: true, kein_fach: !fach, ...(zeiten ? { message: zeiten.slice(3) } : {}) });
      if (!r.ok) notiz('rueckfall', code, { reason: r.reason || 'unbekannt', message: 'Annahme: in PaketPilot angelegt, in Vinted nicht bestätigt' });
      // Angenommen ohne Fach (kein Name): Warnton + Hinweis in der Zeile, statt still ohne Vak zu buchen
      const keinFach = !fach;
      (r.ok ? (keinFach ? tonWarn : tonOk) : tonFehler)();
      setze(code, { stand: r.ok ? 'ok' : 'ohne', name, fach, keinFach, grund: r.reason || '' });
      const ausnahme = !r.ok ? 'rueckfall' : keinFach ? 'keinFach' : !(r.name) ? 'keinName' : '';
      turboMelden({ fach, name, kurz: kurzAnzeige, ausnahme }, tAngelegt - t0);
    } catch (e) {
      tonFehler();
      notiz('annahme_paketpilot', code, { ok: false, message: String((e && e.message) || e).slice(0, 200) });
      setze(code, { stand: 'ohne', name, grund: 'paketpilot' });
      turboMelden({ fach, name, kurz: kurzAnzeige, ausnahme: 'rueckfall' });
    }
  };

  // „Schon gescannt": Etikett für das vorhandene Paket noch einmal drucken (Wunsch Folkert 07.10.2026). Trägt das Paket
  // noch den Platzhalter-Namen, vorher den Namen bei Vinted nachschlagen (nur lesen, keine Buchung).
  const drucken = async (code) => {
    const kurz = normCode(code);
    let p = (paketeRef.current || []).find((x) => x && !x.deleted && !x.ausgegebenAm && !x.zurueckgeschickt && vintedCode(x) === kurz);
    if (!p) return;
    if (platzhalterName(kurz) === String(p.name || '').trim() && namenRef.current) {
      const r = await vintedLookup(code);
      if (r && r.found && r.name) { try { await namenRef.current([{ paket: p, code: kurz, name: r.name }]); p = { ...p, name: r.name }; } catch { /* Platzhalter bleibt */ } }
    }
    window.dispatchEvent(new CustomEvent('kiosk:reprint-label', { detail: p }));
    setze(code, { name: p.name, fach: p.regalplatz || '', gedruckt: true });
  };

  // Rückfall-Eintrag noch einmal an Vinted geben (das Paket steht schon als Platzhalter in PaketPilot). Klappt es,
  // bekommt der Platzhalter den Namen aus Vinted. Ohne Foto: Vinted bekommt dann den erzeugten Code.
  const nochmal = (code) => {
    setze(code, { stand: 'wartet', grund: '' });
    kette.current = kette.current.then(async () => {
      const r = await mitWiederholung(() => vintedIntake(code), { onWarte: ({ versuch }) => setze(code, { stand: 'wartet', grund: T('serverWarte', { n: versuch }) }) });
      if (r.reason === 'fremd') { tonFehler(); setze(code, { stand: 'fremd', grund: '' }); return; }
      if (!r.ok) { tonFehler(); setze(code, { stand: 'ohne', grund: r.reason || '' }); return; }
      tonOk();
      const kurz = normCode(code);
      const p = (paketeRef.current || []).find((x) => x && !x.deleted && !x.ausgegebenAm && !x.zurueckgeschickt && vintedCode(x) === kurz);
      if (p && r.name && namenRef.current) { try { await namenRef.current([{ paket: p, code: kurz, name: r.name }]); } catch { /* Name bleibt Platzhalter */ } }
      notiz('annahme_paketpilot', code, { ok: true, message: 'nach Rückfall erneut an Vinted gegeben' });
      setze(code, { stand: 'ok', name: r.name || platzhalterName(kurz), grund: '' });
    }).catch(() => {});
  };

  const neuerCode = (roh, foto, vollFoto, alle) => {
    const kandidaten = (Array.isArray(alle) && alle.length ? alle : [roh])
      .map((x) => String(x || '').trim().toUpperCase().replace(/\s+/g, ''))
      .filter((x) => x.length >= 4);
    const code = kandidaten.find((x) => istPaketCode(x) || istGlsCode(x)) || kandidaten[0] || '';
    if (code.length < 4 || gesehen.current.has(code)) return;
    gesehen.current.add(code);
    scanZeit.current.set(code, Date.now());
    letzteScanAktivitaet.current = Date.now();
    if (foto || vollFoto) fotos.current.set(code, { foto: foto || vollFoto || '', vollFoto: vollFoto || foto || '' });
    // Liegt das Paket schon offen in PaketPilot? Dann nichts tun (kein Doppel, keine zweite Buchung).
    const kurz = normCode(code);
    const schonDa = (istPaketCode(code) || istGlsCode(code)) && (paketeRef.current || []).some((p) => p && !p.deleted && !p.ausgegebenAm && !p.zurueckgeschickt && vintedCode(p) === kurz);
    if (schonDa && turbo) {
      const p = (paketeRef.current || []).find((x) => x && !x.deleted && !x.ausgegebenAm && !x.zurueckgeschickt && vintedCode(x) === kurz);
      tonWarn();
      turboMelden({ fach: (p && p.regalplatz) || '—', name: (p && p.name) || '', kurz: formatCode(code), ausnahme: 'doppelt' });
    }
    setEintraege((alt) => [{ code, stand: schonDa ? 'doppelt' : 'wartet', name: '', grund: '' }, ...alt]);
    if (!schonDa) {
      aktiveScans.current++;
      kette.current = kette.current
        .then(() => verarbeite(code))
        .catch(() => {})
        .finally(() => { aktiveScans.current = Math.max(0, aktiveScans.current - 1); })
        .then(vorladenNachPause);
    }
  };

  const standText = (e) => ({ wartet: `⏳ ${T('wartet')}`, ok: `✅ ${T(e.grund === 'name_unbekannt' ? 'okOhneName' : 'okVinted')}`, ohne: `⚠️ ${T('ohneVinted')}`, sack: `✅ ${T('sack')}`, sackFehler: `⚠️ ${T('sackFehler')}`, doppelt: `↩️ ${T('doppelt')}`, fremd: `🚚 ${T('fremd')}` }[e.stand]);
  const feld = { width: '100%', padding: '10px', borderRadius: 8, border: `1px solid ${f.rand}`, fontSize: 15, background: f.feld, color: f.text, boxSizing: 'border-box' };
  const laeuft = eintraege.some((e) => e.stand === 'wartet');

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 7600, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div data-testid="vinted-erfassen" style={{ background: dark ? '#23272f' : '#fff', color: f.text, borderRadius: 14, width: '100%', maxWidth: 560, maxHeight: '92vh', overflowY: 'auto', padding: 18, boxShadow: '0 10px 40px rgba(0,0,0,0.4)' }}>
        <b style={{ fontSize: 18 }}>🧪 {T('erfassenTitel')}</b>
        <div data-testid="ve-live" style={{ marginTop: 8, padding: '6px 10px', borderRadius: 8, background: '#c62828', color: '#fff', fontSize: 13, fontWeight: 700 }}>🔴 {T('liveBadge')}</div>
        <div style={{ fontSize: 13, color: f.sub, marginTop: 6, lineHeight: 1.5 }}>{T('erfassenHinweis')}</div>
        {turbo && <TurboAnzeige language={language} dark={dark} letzter={turboStand.letzter} gebucht={turboStand.gebucht} ausnahmen={turboStand.ausnahmen} schnittMs={turboStand.n ? turboStand.summeMs / turboStand.n : 0} wartend={eintraege.filter((e) => e.stand === 'wartet').length} />}
        {vorText && <div data-testid="ve-vorladen" data-stand={vor.laeuft ? 'laeuft' : vor.ok ? 'ok' : 'fehler'} style={{ marginTop: 8, padding: '8px 10px', borderRadius: 8, background: f.zeile, fontSize: 13, lineHeight: 1.5 }}>{vorText}</div>}

        <FotoStand stand={fotoStand} dark={dark} text={(a, b) => T('fotosLaden', { fertig: a, gesamt: b })} fehlerText={(n) => T('fotosFehler', { n })} />
        <input data-testid="ve-fach" placeholder={isVintedSortOn() || isAutoRegalLetter() ? T('fachAuto') : T('fach')} value={fach} onChange={(e) => setFach(e.target.value)} style={{ ...feld, marginTop: 12 }} />
        <div style={{ marginTop: 12 }}>
          <ShelfQrScanner active eingebettet mitFoto vollbildMoeglich formats={['qr_code', 'code_128', 'itf', 'data_matrix', 'code_39', 'ean_13', 'ean_8', 'codabar']} onCode={(text, alle, foto, vollFoto) => neuerCode(text, foto, vollFoto, alle)}
            vollbildInfo={eintraege.length || fotoStand.gesamt ? [<FotoStand key="fotos" hell stand={fotoStand} text={(a, b) => T('fotosLaden', { fertig: a, gesamt: b })} fehlerText={(n) => T('fotosFehler', { n })} />, ...eintraege.slice(0, 3).map((e, i) => (
              <div key={e.code} style={{ padding: '5px 0', fontSize: i === 0 ? 17 : 14, opacity: i === 0 ? 1 : 0.75, borderTop: i ? '1px solid rgba(255,255,255,0.15)' : 'none' }}>
                <b>{istPaketCode(e.code) || istGlsCode(e.code) ? formatCode(e.code) : e.code}</b>{e.name ? ` · ${e.name}` : ''}{e.fach ? ` · 📍 ${e.fach}` : ''}
                <div style={{ fontSize: 13, color: e.stand === 'ohne' || e.stand === 'sackFehler' || e.stand === 'fremd' || e.keinFach ? '#ff8a80' : '#cfd8dc' }}>{standText(e)}{e.keinFach ? ` · ⚠️ ${T('keinFach')}` : ''}</div>
              </div>
            ))] : null} />
        </div>
        <form onSubmit={(e) => { e.preventDefault(); neuerCode(hand); setHand(''); }} style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          <input data-testid="ve-hand" placeholder={T('codeTippen')} value={hand} onChange={(e) => setHand(e.target.value)} style={feld} />
          <button type="submit" data-testid="ve-hand-ok" style={{ padding: '0 14px', borderRadius: 8, border: 'none', background: '#007782', color: '#fff', fontWeight: 600, cursor: 'pointer' }}>{T('hinzufuegen')}</button>
        </form>

        <div data-testid="ve-liste" style={{ marginTop: 12 }}>
          {eintraege.map((e) => (
            <div key={e.code} data-stand={e.stand} style={{ padding: '8px 10px', borderRadius: 8, background: f.zeile, marginTop: 6, fontSize: 14 }}>
              <b>{istPaketCode(e.code) || istGlsCode(e.code) ? formatCode(e.code) : e.code}</b>{e.name ? ` · ${e.name}` : ''}{e.fach ? ` · 📍 ${e.fach}` : ''}
              <div style={{ fontSize: 13, color: e.stand === 'ohne' || e.stand === 'sackFehler' || e.stand === 'fremd' || e.keinFach ? '#c62828' : f.sub, marginTop: 2, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ flex: 1 }}>{standText(e)}{e.keinFach ? ` · ⚠️ ${T('keinFach')}` : ''}{e.grund && e.stand !== 'ok' && e.stand !== 'sack' ? ` (${e.grund})` : ''}</span>
                {e.stand === 'doppelt' && (istPaketCode(e.code) || istGlsCode(e.code)) && (
                  <button type="button" data-testid={`ve-drucken-${e.code}`} onClick={() => drucken(e.code)} style={{ padding: '4px 10px', borderRadius: 8, border: `1px solid ${f.rand}`, background: 'transparent', color: f.text, fontSize: 13, cursor: 'pointer' }}>🖨️ {e.gedruckt ? T('gedruckt') : T('drucken')}</button>
                )}
                {e.stand === 'ohne' && (istPaketCode(e.code) || istGlsCode(e.code)) && (
                  <button type="button" data-testid={`ve-nochmal-${e.code}`} onClick={() => nochmal(e.code)} style={{ padding: '4px 10px', borderRadius: 8, border: `1px solid ${f.rand}`, background: 'transparent', color: f.text, fontSize: 13, cursor: 'pointer' }}>↻ {T('nochmal')}</button>
                )}
              </div>
            </div>
          ))}
        </div>

        <button type="button" data-testid="ve-schliessen" disabled={laeuft} onClick={() => { const n = fotosOffen(fotoStand); if (n && !window.confirm(T('fotosOffen', { n }))) return; onClose(); }} style={{ width: '100%', padding: '12px', borderRadius: 10, marginTop: 14, fontSize: 15, fontWeight: 600, cursor: laeuft ? 'default' : 'pointer', border: 'none', background: '#007782', color: '#fff', opacity: laeuft ? 0.6 : 1 }}>
          {laeuft ? `⏳ ${T('wartet')}` : T('fertig')}
        </button>
      </div>
    </div>
  );
}
